import { useSyncExternalStore } from 'react';
import { type Health, healthSchema } from '@xphoto/shared';

/**
 * État de l'appel `/api/health` partagé (AboutFooter + footer version du menu
 * de compte) : `null` tant que la requête n'a pas abouti, `Health` une fois
 * validé par Zod, `error` si l'API est injoignable ou la réponse invalide.
 */
export interface HealthState {
  health: Health | null;
  error: boolean;
}

// --- Source PARTAGÉE ----------------------------------------------------------
//
// ⚠ PERF (audit A7) : `/api/health` était un hook PAR INSTANCE. Le JSDoc affirmait
// « réutilisable sans dupliquer le fetch » — c'était FAUX, rien ne dédoublonnait. Deux
// appelants sont montés en permanence (`App` via `useDocumentTitle`, et
// `auth/AccountSwitcher` dans la coquille), un 3ᵉ s'ajoute sur `/admin` → 2 à 3
// `GET /api/health` par chargement de page, chacun faisant une requête DB côté serveur.
//
// Même patron que `CountsSource` (`gallery/use-facets.ts`) : un store module-level lu
// par `useSyncExternalStore`, avec COMPTAGE DE RÉFÉRENCES — la requête part au premier
// abonné et l'état est remis à zéro quand le dernier se démonte (le prochain montage
// reflète un serveur éventuellement redéployé). La signature du hook est inchangée :
// aucun appelant à toucher.

const INITIAL: HealthState = { health: null, error: false };

class HealthSource {
  private state: HealthState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private refCount = 0;
  private controller: AbortController | null = null;
  private loaded = false;

  getSnapshot = (): HealthState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    this.refCount += 1;
    if (this.refCount === 1 && !this.loaded) {
      this.loaded = true;
      this.fetch();
    }
    return () => {
      this.listeners.delete(listener);
      this.refCount -= 1;
      if (this.refCount === 0) this.reset();
    };
  };

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private fetch(): void {
    const controller = new AbortController();
    this.controller = controller;
    fetch('/api/health', { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (controller.signal.aborted) return;
        this.state = { health: healthSchema.parse(data), error: false };
        this.emit();
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        this.state = { health: null, error: true };
        this.emit();
      });
  }

  private reset(): void {
    this.controller?.abort();
    this.controller = null;
    this.loaded = false;
    this.state = INITIAL;
  }
}

const source = new HealthSource();

/**
 * Récupère `/api/health` et re-parse la réponse via `healthSchema`. UNE SEULE requête
 * est émise quels que soient le nombre d'appelants montés simultanément (source
 * partagée ci-dessus) ; elle est annulée quand le dernier appelant se démonte.
 */
export function useHealth(): HealthState {
  return useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
}
